import React from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../../shared/components';
import { useToast } from '../../notifications';
import {
  deleteKnowledgeDocumentAttachment,
  getKnowledgeAttachmentSettings,
  listKnowledgeDocumentAttachments,
  reorderKnowledgeDocumentAttachments,
  updateKnowledgeDocumentAttachment,
  uploadKnowledgeDocumentAttachment,
} from '../services/knowledgeService';
import type { KnowledgeAttachment, KnowledgeAttachmentSettings, KnowledgeDocument } from '../types/Knowledge';
import {
  DEFAULT_KNOWLEDGE_MAX_IMAGE_BYTES,
  DEFAULT_KNOWLEDGE_MAX_VIDEO_BYTES,
  getKnowledgeAttachmentAccept,
  getKnowledgeAttachmentTypesLabel,
  isKnowledgeMediaFile,
  isKnowledgeVideoAttachment,
  isStructuredKnowledgeDocument,
} from '../types/Knowledge';
import styles from './KnowledgeDocumentAttachmentsPanel.module.css';

interface KnowledgeDocumentAttachmentsPanelProps {
  document: KnowledgeDocument;
  canManage: boolean;
  onAttachmentCountChange?: (documentId: string, count: number) => void;
}

const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value >= 10 || unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
};

export const KnowledgeDocumentAttachmentsPanel: React.FC<KnowledgeDocumentAttachmentsPanelProps> = ({
  document,
  canManage,
  onAttachmentCountChange,
}) => {
  const { t } = useTranslation(['knowledge', 'common']);
  const { showToast } = useToast();
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const [attachments, setAttachments] = React.useState<KnowledgeAttachment[]>([]);
  const [settings, setSettings] = React.useState<KnowledgeAttachmentSettings | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [reordering, setReordering] = React.useState(false);
  const [busyAttachmentId, setBusyAttachmentId] = React.useState<string | null>(null);
  const [captionDrafts, setCaptionDrafts] = React.useState<Record<string, string>>({});
  const [draggingAttachmentId, setDraggingAttachmentId] = React.useState<string | null>(null);
  const [dropTargetAttachmentId, setDropTargetAttachmentId] = React.useState<string | null>(null);
  const [isDropZoneActive, setIsDropZoneActive] = React.useState(false);

  const isStructured = isStructuredKnowledgeDocument(document);
  const attachmentsEnabled = settings?.enabled !== false;

  const loadAttachments = React.useCallback(async () => {
    setLoading(true);
    try {
      const [attachmentItems, attachmentSettings] = await Promise.all([
        listKnowledgeDocumentAttachments(document.id),
        getKnowledgeAttachmentSettings(),
      ]);
      setAttachments(attachmentItems);
      setSettings(attachmentSettings);
      setCaptionDrafts(Object.fromEntries(attachmentItems.map(item => [item.id, item.caption ?? ''])));
      onAttachmentCountChange?.(document.id, attachmentItems.length);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('attachments.failedToLoad', { ns: 'knowledge' }), 'error');
    } finally {
      setLoading(false);
    }
  }, [document.id, onAttachmentCountChange, showToast, t]);

  React.useEffect(() => {
    if (!isStructured) {
      void loadAttachments();
      return;
    }

    setLoading(false);
    setAttachments([]);
    setSettings(null);
  }, [isStructured, loadAttachments]);

  const uploadFiles = async (files: File[]) => {
    if (files.length === 0 || !canManage || isStructured || !attachmentsEnabled) {
      return;
    }

    const maxAttachments = settings?.maxAttachmentsPerDocument ?? 20;
    const remainingSlots = Math.max(0, maxAttachments - attachments.length);
    if (remainingSlots <= 0) {
      showToast(t('attachments.limitReached', { ns: 'knowledge', count: maxAttachments }), 'error');
      return;
    }

    const filesToUpload = files.slice(0, remainingSlots);
    setUploading(true);

    try {
      for (const file of filesToUpload) {
        await uploadKnowledgeDocumentAttachment(document.id, { file });
      }

      showToast(
        filesToUpload.length === 1
          ? t('attachments.uploadSuccess', { ns: 'knowledge' })
          : t('attachments.uploadSuccessMultiple', { ns: 'knowledge', count: filesToUpload.length }),
        'success',
      );
      await loadAttachments();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('attachments.failedToUpload', { ns: 'knowledge' }), 'error');
    } finally {
      setUploading(false);
    }
  };

  const onPickFiles = () => {
    fileInputRef.current?.click();
  };

  const onUploadFiles = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []).filter(isKnowledgeMediaFile);
    event.target.value = '';
    await uploadFiles(files);
  };

  const onDropZoneDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    if (!canManage || !attachmentsEnabled) {
      return;
    }

    setIsDropZoneActive(true);
  };

  const onDropZoneDragLeave = () => {
    setIsDropZoneActive(false);
  };

  const onDropZoneDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    setIsDropZoneActive(false);

    const files = Array.from(event.dataTransfer.files ?? []).filter(isKnowledgeMediaFile);
    await uploadFiles(files);
  };

  const persistAttachmentOrder = async (nextAttachments: KnowledgeAttachment[]) => {
    setReordering(true);
    try {
      const reordered = await reorderKnowledgeDocumentAttachments(
        document.id,
        nextAttachments.map(item => item.id),
      );
      setAttachments(reordered);
      onAttachmentCountChange?.(document.id, reordered.length);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('attachments.failedToReorder', { ns: 'knowledge' }), 'error');
      await loadAttachments();
    } finally {
      setReordering(false);
    }
  };

  const moveAttachment = async (attachmentId: string, direction: -1 | 1) => {
    const currentIndex = attachments.findIndex(item => item.id === attachmentId);
    const targetIndex = currentIndex + direction;
    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= attachments.length) {
      return;
    }

    const next = [...attachments];
    const [moved] = next.splice(currentIndex, 1);
    next.splice(targetIndex, 0, moved);
    setAttachments(next);
    await persistAttachmentOrder(next);
  };

  const onAttachmentDragStart = (attachmentId: string) => {
    setDraggingAttachmentId(attachmentId);
  };

  const onAttachmentDragOver = (event: React.DragEvent, attachmentId: string) => {
    event.preventDefault();
    if (!draggingAttachmentId || draggingAttachmentId === attachmentId) {
      return;
    }

    setDropTargetAttachmentId(attachmentId);
  };

  const onAttachmentDrop = async (attachmentId: string) => {
    if (!draggingAttachmentId || draggingAttachmentId === attachmentId) {
      setDraggingAttachmentId(null);
      setDropTargetAttachmentId(null);
      return;
    }

    const sourceIndex = attachments.findIndex(item => item.id === draggingAttachmentId);
    const targetIndex = attachments.findIndex(item => item.id === attachmentId);
    if (sourceIndex < 0 || targetIndex < 0) {
      return;
    }

    const next = [...attachments];
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, moved);
    setAttachments(next);
    setDraggingAttachmentId(null);
    setDropTargetAttachmentId(null);
    await persistAttachmentOrder(next);
  };

  const onSaveCaption = async (attachment: KnowledgeAttachment) => {
    if (!canManage) {
      return;
    }

    setBusyAttachmentId(attachment.id);
    try {
      const updated = await updateKnowledgeDocumentAttachment(document.id, attachment.id, {
        caption: captionDrafts[attachment.id] ?? '',
      });
      setAttachments(current => current.map(item => (item.id === updated.id ? updated : item)));
      showToast(t('attachments.captionSaved', { ns: 'knowledge' }), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('attachments.failedToSaveCaption', { ns: 'knowledge' }), 'error');
    } finally {
      setBusyAttachmentId(null);
    }
  };

  const onDeleteAttachment = async (attachment: KnowledgeAttachment) => {
    if (!canManage) {
      return;
    }

    if (!window.confirm(t('attachments.deleteConfirm', { ns: 'knowledge', fileName: attachment.fileName }))) {
      return;
    }

    setBusyAttachmentId(attachment.id);
    try {
      await deleteKnowledgeDocumentAttachment(document.id, attachment.id);
      await loadAttachments();
      showToast(t('attachments.deleteSuccess', { ns: 'knowledge' }), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('attachments.failedToDelete', { ns: 'knowledge' }), 'error');
    } finally {
      setBusyAttachmentId(null);
    }
  };

  if (isStructured) {
    return (
      <div className={styles.panel}>
        <p className={styles.muted}>{t('attachments.structuredNotSupported', { ns: 'knowledge' })}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.panel}>
        <Spinner text={t('attachments.loading', { ns: 'knowledge' })} />
      </div>
    );
  }

  const maxAttachments = settings?.maxAttachmentsPerDocument ?? 20;
  const maxImageBytes = settings?.maxAttachmentBytes ?? DEFAULT_KNOWLEDGE_MAX_IMAGE_BYTES;
  const maxVideoBytes = settings?.maxVideoAttachmentBytes ?? DEFAULT_KNOWLEDGE_MAX_VIDEO_BYTES;
  const allowedTypes = getKnowledgeAttachmentTypesLabel(settings);
  const acceptedTypes = getKnowledgeAttachmentAccept(settings);

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div>
          <h4 className={styles.title}>{t('attachments.title', { ns: 'knowledge' })}</h4>
          <p className={styles.help}>
            {t('attachments.help', {
              ns: 'knowledge',
              count: maxAttachments,
              imageSize: formatFileSize(maxImageBytes),
              videoSize: formatFileSize(maxVideoBytes),
              types: allowedTypes,
            })}
          </p>
        </div>
        {canManage ? (
          <div className={styles.headerActions}>
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptedTypes}
              multiple
              className={styles.hiddenInput}
              onChange={(event) => void onUploadFiles(event)}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onPickFiles}
              disabled={uploading || reordering || !attachmentsEnabled || attachments.length >= maxAttachments}
            >
              {uploading ? t('attachments.uploading', { ns: 'knowledge' }) : t('attachments.addImages', { ns: 'knowledge' })}
            </button>
          </div>
        ) : null}
      </div>

      {canManage && attachmentsEnabled && attachments.length < maxAttachments ? (
        <div
          className={`${styles.dropZone} ${isDropZoneActive ? styles.dropZoneActive : ''}`}
          onDragOver={onDropZoneDragOver}
          onDragLeave={onDropZoneDragLeave}
          onDrop={(event) => void onDropZoneDrop(event)}
        >
          {t('attachments.dropZone', { ns: 'knowledge' })}
        </div>
      ) : null}

      {!attachmentsEnabled ? (
        <p className={styles.muted}>{t('attachments.disabled', { ns: 'knowledge' })}</p>
      ) : attachments.length === 0 ? (
        <p className={styles.muted}>{t('attachments.empty', { ns: 'knowledge' })}</p>
      ) : (
        <div className={styles.gallery}>
          {attachments.map((attachment, index) => {
            const busy = busyAttachmentId === attachment.id;
            const previewUrl = attachment.accessibleUrl || attachment.mediaUrl;
            const isDragging = draggingAttachmentId === attachment.id;
            const isDropTarget = dropTargetAttachmentId === attachment.id;

            return (
              <article
                key={attachment.id}
                className={`${styles.card} ${isDragging ? styles.cardDragging : ''} ${isDropTarget ? styles.cardDropTarget : ''}`}
                draggable={canManage && !reordering}
                onDragStart={() => onAttachmentDragStart(attachment.id)}
                onDragEnd={() => {
                  setDraggingAttachmentId(null);
                  setDropTargetAttachmentId(null);
                }}
                onDragOver={(event) => onAttachmentDragOver(event, attachment.id)}
                onDrop={() => void onAttachmentDrop(attachment.id)}
              >
                {canManage ? (
                  <div className={styles.reorderControls}>
                    <button
                      type="button"
                      className={styles.reorderButton}
                      disabled={busy || reordering || index === 0}
                      onClick={() => void moveAttachment(attachment.id, -1)}
                      aria-label={t('attachments.moveEarlier', { ns: 'knowledge' })}
                    >
                      ←
                    </button>
                    <span className={styles.dragHint}>{t('attachments.dragToReorder', { ns: 'knowledge' })}</span>
                    <button
                      type="button"
                      className={styles.reorderButton}
                      disabled={busy || reordering || index === attachments.length - 1}
                      onClick={() => void moveAttachment(attachment.id, 1)}
                      aria-label={t('attachments.moveLater', { ns: 'knowledge' })}
                    >
                      →
                    </button>
                  </div>
                ) : null}
                {isKnowledgeVideoAttachment(attachment.mimeType, attachment.fileName) ? (
                  <div className={styles.previewMedia}>
                    <video src={previewUrl} className={styles.previewVideo} controls preload="metadata" />
                  </div>
                ) : (
                  <a href={previewUrl} target="_blank" rel="noreferrer" className={styles.previewLink}>
                    <img src={previewUrl} alt={attachment.altText || attachment.caption || attachment.fileName} className={styles.preview} />
                  </a>
                )}
                <div className={styles.cardBody}>
                  <div className={styles.fileMeta}>
                    <strong>{attachment.fileName}</strong>
                    <span>{formatFileSize(attachment.fileSize)}</span>
                    {isKnowledgeVideoAttachment(attachment.mimeType, attachment.fileName) ? (
                      <a href={previewUrl} target="_blank" rel="noreferrer" className={styles.openMediaLink}>
                        {t('attachments.openInNewTab', { ns: 'knowledge' })}
                      </a>
                    ) : null}
                  </div>
                  {canManage ? (
                    <>
                      <label className={styles.captionField}>
                        <span>{t('attachments.captionLabel', { ns: 'knowledge' })}</span>
                        <input
                          type="text"
                          value={captionDrafts[attachment.id] ?? ''}
                          maxLength={settings?.maxCaptionLength ?? 500}
                          disabled={busy || reordering}
                          onChange={(event) => setCaptionDrafts(current => ({ ...current, [attachment.id]: event.target.value }))}
                        />
                      </label>
                      <div className={styles.cardActions}>
                        <button type="button" className="btn btn-secondary" disabled={busy || reordering} onClick={() => void onSaveCaption(attachment)}>
                          {busy ? t('working', { ns: 'knowledge' }) : t('attachments.saveCaption', { ns: 'knowledge' })}
                        </button>
                        <button type="button" className="btn btn-secondary" disabled={busy || reordering} onClick={() => void onDeleteAttachment(attachment)}>
                          {t('delete', { ns: 'common' })}
                        </button>
                      </div>
                    </>
                  ) : attachment.caption ? (
                    <p className={styles.captionText}>{attachment.caption}</p>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};
